import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import {
  ExtendedBackfillMetricsState,
  ExtendedBackfillStatus,
  ExtendedBackfillResult,
} from '@/types/userSyncTypes';

const initialState = {
  sessionId: null,
  status: null,
  result: null,
  error: null, // Error is not persisted - it's cleared on refresh to prevent stale errors
  isRunning: false,
  isCompleted: false,
  isDismissed: false,
  syncType: null as 'backfill' | 'user_sync' | null,
};

export const useExtendedBackfillMetricsStore =
  create<ExtendedBackfillMetricsState>()(
    devtools(
      persist(
        (set, get) => ({
          ...initialState,

          // Actions
          setSessionId: (sessionId: string) =>
            set({ sessionId }, false, 'setSessionId'),

          setStatus: (status: ExtendedBackfillStatus) => {
            return set({ status }, false, 'setStatus');
          },

          setResult: (result: ExtendedBackfillResult) => {
            return set({ result, isCompleted: true }, false, 'setResult');
          },

          setError: (error: string | null) =>
            set({ error, isRunning: false }, false, 'setError'),

          setIsRunning: (isRunning: boolean) =>
            set({ isRunning }, false, 'setIsRunning'),

          setIsCompleted: (isCompleted: boolean) =>
            set({ isCompleted }, false, 'setIsCompleted'),

          setIsDismissed: (isDismissed: boolean) =>
            set({ isDismissed }, false, 'setIsDismissed'),

          setSyncType: (syncType: 'backfill' | 'user_sync') =>
            set({ syncType }, false, 'setSyncType'),

          reset: () => {
            return set(initialState, false, 'reset');
          },

          // Progress helpers
          getProgressPercentage: () => {
            const { status } = get();
            return status ? Math.round(status.progress * 100) : 0;
          },

          getCurrentStageMessage: () => {
            const { status } = get();
            if (!status) return '';

            const stageMessages: Record<string, string> = {
              // Backfill stages
              initialization: 'Initializing backfill process...',
              team_validation: 'Validating team information...',
              user_fetching: 'Fetching team members...',
              chart_initialization: 'Initializing charts...',
              pr_processing: 'Processing pull requests...',
              pr_search: 'Searching for pull requests...',
              pr_discovery: 'Discovering pull requests...',
              processing_pr: 'Processing individual pull request...',
              pr_individual: 'Processing individual PRs...',
              batch_processing: 'Processing batch of PRs...',
              chart_update: 'Updating charts...',
              metrics_aggregation: 'Aggregating metrics...',
              chart_generation: 'Generating charts...',
              completion: 'Backfill completed successfully!',

              // User sync stages
              user_validation: 'Validating user information...',
              integration_setup: 'Setting up integrations...',
              date_calculation: 'Calculating date ranges...',
              github_processing: 'Processing GitHub data...',
              gitlab_processing: 'Processing GitLab data...',
              finalization: 'User sync completed successfully!',
            };

            return (
              stageMessages[status.stage] || status.message || 'Processing...'
            );
          },

          hasError: () => {
            const { error } = get();
            return !!error;
          },
        }),
        {
          name: 'extended-backfill-metrics-storage',
          partialize: (state) => ({
            sessionId: state.sessionId,
            isDismissed: state.isDismissed,
            status: state.status, // Persist status to restore progress
            syncType: state.syncType,
            isRunning: state.isRunning,
            isCompleted: state.isCompleted,
            // Don't persist error - errors should be fresh, not stale from previous sessions
            // This prevents showing errors on refresh before we check backend status
            // error: state.error,
          }),
        }
      )
    )
  );

// Selectors for performance optimization
export const selectExtendedBackfillStatus = (
  state: ExtendedBackfillMetricsState
) => state.status;
export const selectExtendedBackfillResult = (
  state: ExtendedBackfillMetricsState
) => state.result;
export const selectExtendedBackfillError = (
  state: ExtendedBackfillMetricsState
) => state.error;
export const selectIsExtendedBackfillRunning = (
  state: ExtendedBackfillMetricsState
) => state.isRunning;
export const selectIsExtendedBackfillCompleted = (
  state: ExtendedBackfillMetricsState
) => state.isCompleted;
export const selectExtendedBackfillProgress = (
  state: ExtendedBackfillMetricsState
) => state.getProgressPercentage();
